import React, { useState, useCallback } from 'react';
import texasApi from '../../services/texasApi';

// FE-TX-UI-RESTORE: render API responses as a readable key/value grid instead of
// a raw JSON dump. Shape-agnostic on purpose — each SB 38 endpoint returns a
// different payload shape (timelines, trial window, service deadline, writ,
// summary disposition, sworn declaration).
const humanizeKey = (key: string): string =>
  key
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/^./, (c) => c.toUpperCase());

const formatValue = (value: unknown): string => {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return JSON.stringify(value);
};

// UX-8: SB 38 results used to be printed as a raw JSON dump. Render the sb38
// payload as human-readable timeline cards (human dates, event titles, status
// badges) — the raw payload stays one <details> tap away, never the default.
const TITLE_KEYS = new Set(['eventType', 'type', 'label', 'title', 'name', 'step', 'milestone', 'stage']);
const DATE_KEYS = new Set([
  'filingDate', 'trialDate', 'servingDate', 'hearingDate', 'writDate', 'executionDate',
  'noticeDate', 'deadline', 'date', 'scheduledFor', 'notificationDate',
  'answerDeadline', 'possessionDate', 'renewalDate', 'expiryDate', 'nextTrialDate',
]);
const STATUS_KEYS = new Set(['status', 'result', 'outcome', 'state', 'phase', 'disposition']);
const IGNORE_KEYS = new Set(['success', 'successMessage', 'message', 'error', 'caseId', 'propertyId']);

const humanizeDate = (value: unknown): string => {
  if (typeof value !== 'string') return String(value ?? '—');
  const t = value.trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(t)) {
    const d = new Date(t);
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
    }
  }
  return t || '—';
};

const toEventArray = (payload: any): any[] => {
  const data = payload && typeof payload === 'object' && payload.data
    ? payload.data : payload;
  if (Array.isArray(data)) return data;
  if (data && typeof data === 'object') {
    if (Array.isArray(data.timelines)) return data.timelines;
    if (Array.isArray(data.events)) return data.events;
  }
  return data && typeof data === 'object' ? [data] : [];
};

const statusBadge = (value: unknown): { text: string; cls: string } => {
  const s = String(value ?? '');
  const low = s.toLowerCase();
  if (low === 'active' || low === 'eligible' || low === 'completed' || low === 'yes') {
    return { text: s, cls: 'bg-emerald-100 text-emerald-700' };
  }
  if (low === 'expired' || low === 'missed' || low === 'no' || low === 'past' || low === 'violation') {
    return { text: s, cls: 'bg-red-100 text-red-700' };
  }
  if (low.includes('pending') || low.includes('waiting') || low.includes('expiring')) {
    return { text: s, cls: 'bg-amber-100 text-amber-800' };
  }
  return { text: s || 'Pending', cls: 'bg-slate-100 text-slate-600' };
};

export const SB38NoticeGenerator: React.FC = () => {
  const [caseId, setCaseId] = useState<string>('');
  const [filingDate, setFilingDate] = useState<string>('');
  const [writIssuanceDate, setWritIssuanceDate] = useState<string>('');
  const [result, setResult] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const calculateTimelines = useCallback(async () => {
    if (!caseId.trim() || !filingDate) return;
    setLoading(true);
    setError(null);
    setResult(null);
    try {
      const timelines = await texasApi.sb38.getTimelines({
        filingDate,
        writIssuanceDate: writIssuanceDate || undefined,
        caseType: 'eviction',
      });
      setResult(timelines);
    } catch (err: any) {
      setError(err?.message || 'Failed to calculate SB 38 timelines');
    } finally {
      setLoading(false);
    }
  }, [caseId, filingDate, writIssuanceDate]);

  const calculateTrial = async () => {
    if (!caseId.trim() || !filingDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.calculateTrial({ filingDate, caseType: 'eviction' });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to calculate trial window');
    } finally {
      setLoading(false);
    }
  };

  const calculateService = async () => {
    if (!caseId.trim() || !filingDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.calculateService({ filingDate, caseType: 'eviction' });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to calculate service deadline');
    } finally {
      setLoading(false);
    }
  };

  const calculateWrit = async () => {
    if (!caseId.trim() || !writIssuanceDate) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.calculateWrit({
        writIssuanceDate,
        caseType: 'eviction',
      });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to calculate writ deadline');
    } finally {
      setLoading(false);
    }
  };

  const summarize = async () => {
    if (!caseId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.summarize({ caseId });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to summarize disposition eligibility');
    } finally {
      setLoading(false);
    }
  };

  const recordSworn = async () => {
    if (!caseId.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const res = await texasApi.sb38.recordSwornDeclaration({ caseId });
      setResult(res);
    } catch (err: any) {
      setError(err?.message || 'Failed to record sworn declaration');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="sb38-notice-generator" className="texas-page-container">
      <h1>SB 38 Notice Generator</h1>
      <p className="page-description">
        Generate and calculate Texas SB 38 accelerated eviction timelines.
      </p>

      <section className="search-bar">
        <div className="field-group">
          <label htmlFor="case-id-input">Case ID</label>
          <input
            id="case-id-input"
            type="text"
            placeholder="Case ID"
            value={caseId}
            onChange={(e) => setCaseId(e.target.value)}
            data-testid="case-id-input"
          />
        </div>
        <div className="field-group">
          <label htmlFor="filing-date-input">Filing Date</label>
          <input
            id="filing-date-input"
            type="date"
            value={filingDate}
            onChange={(e) => setFilingDate(e.target.value)}
            data-testid="filing-date-input"
          />
        </div>
        <div className="field-group">
          <label htmlFor="writ-date-input">Writ Issuance Date (optional)</label>
          <input
            id="writ-date-input"
            type="date"
            value={writIssuanceDate}
            onChange={(e) => setWritIssuanceDate(e.target.value)}
            data-testid="writ-date-input"
          />
        </div>
      </section>

      <section className="action-bar">
        <button onClick={calculateTimelines} disabled={loading} data-testid="calc-timelines-btn">
          {loading ? 'Calculating...' : 'Calculate All Timelines'}
        </button>
        <button onClick={calculateTrial} disabled={loading} data-testid="calc-trial-btn">
          Trial Window (21d)
        </button>
        <button onClick={calculateService} disabled={loading} data-testid="calc-service-btn">
          Service Deadline (5d)
        </button>
        <button onClick={calculateWrit} disabled={loading} data-testid="calc-writ-btn">
          Writ Deadline (5d)
        </button>
        <button onClick={summarize} disabled={loading} data-testid="summarize-btn">
          Summary Disposition
        </button>
        <button onClick={recordSworn} disabled={loading} data-testid="sworn-btn">
          Record Sworn Declaration
        </button>
      </section>

      {error && <div className="error" data-testid="error">{error}</div>}

      {result && (
        <section className="result-section" data-testid="result">
          <h2>SB 38 Timeline Result</h2>
          {toEventArray(result).length === 0 ? (
            <div className="no-results" data-testid="timeline-empty">
              No timeline events returned for this request.
            </div>
          ) : (
            <div className="timeline-card-list" data-testid="timeline-card-list">
              {toEventArray(result).map((evt: any, idx: number) => {
                const keys = Object.keys(evt).filter(
                  (k) => !TITLE_KEYS.has(k) && !DATE_KEYS.has(k) && !STATUS_KEYS.has(k) && !IGNORE_KEYS.has(k)
                );
                const titleKey = Object.keys(evt).find((k) => TITLE_KEYS.has(k));
                const dateKey = Object.keys(evt).find((k) => DATE_KEYS.has(k));
                const statusKey = Object.keys(evt).find((k) => STATUS_KEYS.has(k));
                const badge = statusKey ? statusBadge(evt[statusKey]) : statusBadge('pending');
                return (
                  <div className="timeline-card" key={idx} data-testid={`timeline-card-${idx}`}>
                    <div className="flex items-start gap-3">
                      <span className="timeline-dot" aria-hidden="true"></span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-black text-gray-900 text-sm">
                            {titleKey
                              ? formatValue(evt[titleKey])
                              : humanizeKey(dateKey || `event ${idx + 1}`)}
                          </div>
                          <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${badge.cls}`} data-testid={`timeline-status-${idx}`}>
                            {badge.text}
                          </span>
                        </div>
                        {dateKey && (
                          <div className="text-xs text-slate-500 mt-0.5" data-testid={`timeline-date-${idx}`}>
                            {humanizeDate(evt[dateKey])}
                          </div>
                        )}
                        {keys.length > 0 && (
                          <dl className="timeline-card-details mt-2">
                            {keys.map((k) => (
                              <div key={k} className="timeline-row">
                                <dt>{humanizeKey(k)}</dt>
                                <dd>{typeof evt[k] === 'object' ? formatValue(evt[k]) : humanizeDate(evt[k])}</dd>
                              </div>
                            ))}
                          </dl>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <details className="result-raw">
            <summary>Raw response</summary>
            <pre>{JSON.stringify(result, null, 2)}</pre>
          </details>
        </section>
      )}
    </div>
  );
};

export default SB38NoticeGenerator;
